import store from 'store/Store';
import SuperChatActions from 'ai/actions/superChatActions';
import anaUtils from 'analytics/utils';
import LanguageLocaleUtils from 'utils/LanguageLocale';

// Constants
import { INTENTIONS, ENTRYPOINTS } from 'ai/constants/superchat';
import { CATEGORIES } from 'ai/constants/categories';
import { NAV_SOURCES } from 'ai/analytics/events';
import { consumeSuperChatEntrySource, SUPERCHAT_ENTRY_SOURCES } from 'ai/utils/sessionStorage';

// Locales
import SuperChatEN from 'ai/components/SuperChat/locales/SuperChat_en_US.js';
import SuperChatFR from 'ai/components/SuperChat/locales/SuperChat_fr_CA.js';

const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const getSuperChatText = getLocalisedDataFromResource(SuperChatEN, SuperChatFR);

const VALID_INTENTIONS = Object.values(INTENTIONS);

/**
 * Extracts the SuperChat `intention` (and optional `prompt`) from a Contentful
 * Action shape. The params may live in either `targetUrl` or `urlParameters`
 * (mirrors the detection used in ShopModal). Returns `null` when no valid
 * intention is present so callers can fall through to normal navigation.
 *
 * @param  {Object} action - Contentful Action shape (see Action.f.jsx PropTypes).
 * @return {?{intention: string, prompt: (string|undefined)}}
 */
export const getSuperChatIntentionFromAction = action => {
    if (!action) {
        return null;
    }

    const targetUrl = action.targetUrl || '';
    const urlParameters = action.urlParameters || '';
    const intention = anaUtils.getQueryParam('intention', targetUrl) || anaUtils.getQueryParam('intention', urlParameters);

    if (!intention || !VALID_INTENTIONS.includes(intention)) {
        return null;
    }

    const prompt = anaUtils.getQueryParam('prompt', targetUrl) || anaUtils.getQueryParam('prompt', urlParameters) || undefined;

    return { intention, prompt };
};

/**
 * Opens the AI Beauty Chat (SuperChat) in place for a given intention, WITHOUT
 * navigating away from the current page. The analytics context is built
 * identically to the deeplink handler in withSuperChatProps (one-shot entry
 * source -> source + internalCampaign) so existing tracking is preserved.
 *
 * @param {{intention: string, prompt: (string|undefined)}} params
 */
export const openSuperChatFromIntention = ({ intention, prompt } = {}) => {
    if (!intention) {
        return;
    }

    // One-shot read: only a click that just set this marker (e.g. ShopModal
    // featured link) yields analytics context. Other entry paths leave it empty.
    const entrySource = consumeSuperChatEntrySource();
    const analyticsContext =
        entrySource === SUPERCHAT_ENTRY_SOURCES.SHOP_TAB_FEATURED
            ? {
                source: NAV_SOURCES.SHOP_TAB,
                internalCampaign: `FeaturedLink_beautyAiChat_intention=${(intention || '').toUpperCase()}`
            }
            : {};

    const defaultPrompt = intention === INTENTIONS.GIFT_FINDER ? getSuperChatText('lookingForAGift') : undefined;
    const selectedPrompt = prompt || defaultPrompt || undefined;

    const data =
        intention === INTENTIONS.GIFT_FINDER
            // eslint-disable-next-line camelcase -- category_id matches the SuperChat API payload shape
            ? { entrypoint: ENTRYPOINTS.DEFAULT, category_id: CATEGORIES.GIFT, selectedPrompt }
            : { entrypoint: ENTRYPOINTS.DEFAULT, selectedPrompt };

    if (analyticsContext.source) {
        data.source = analyticsContext.source;
    }

    if (analyticsContext.internalCampaign) {
        data.internalCampaign = analyticsContext.internalCampaign;
    }

    store.dispatch(SuperChatActions.showSuperChat(true, [], data));
};
